import { useAtom } from '@reatom/npm-react'
import { memo } from 'react'
import styled, { css } from 'styled-components'

import { Typography } from '@blade/ui-kit'
import {
  CurrencyInformationClipboard,
  CurrencyInformationSocial,
} from '@root/entities/currency/ui'
import { CurrencyAddressSearchLoupe } from '@root/entities/currency/ui/currency-address-search-loupe'
import { PairBoostersBadge, PairInformationAge } from '@root/entities/pair/ui'
import { PairIcon, PairTicker } from '@root/entities/pair/ui/common'
import { PairCashbackBadge } from '@root/entities/pair/ui/common/pair-cashback-badge'
import { PairTokenizedAgentBadge } from '@root/entities/pair/ui/common/pair-tokenized-agent-badge'
import { PairRank } from '@root/entities/pair/ui/pair-rank'
import { ProviderBadge } from '@root/entities/provider/ui'
import { PairInformationFavorite } from '@root/features/favorite-pairs/ui/desktop'
import { mediaMaxMobile } from '@root/shared/ui-kit'

import type { PairModel } from '@root/entities/pair/atoms/reatom-pair'

type ExploreTablePairCellProps = {
  pair: PairModel
}

export const ExploreTablePairCell = memo(
  ({ pair }: ExploreTablePairCellProps) => {
    const [rank] = useAtom(pair.rank)
    const [socials] = useAtom(pair.quote.socials)
    const [isCashBackMode] = useAtom(pair.isCashBackMode)
    const [isTokenizedAgent] = useAtom(pair.isTokenizedAgent)

    return (
      <PairInformationRoot>
        <Favorite
          pair={pair}
          css={css`
            margin-inline-end: 8px;
          `}
        />
        <PairIcon withPairPopover pair={pair} size={40} />
        <CurrencyInformation>
          <Top>
            <Ticker pair={pair} />
            <PairRank rank={rank} />
            <PairBoostersBadge
              currency={pair.quote}
              premiumInfo={pair.premiumInfo}
              mode="explore"
            />
            <SearchLoupe
              currencyAddress={pair.quote.address}
              symbol={pair.quote.ticker}
              size={16}
            />
            <CurrencyInformationSocial socials={socials} />
            {isCashBackMode ? <PairCashbackBadge size={14} /> : undefined}
            {isTokenizedAgent ? (
              <PairTokenizedAgentBadge size={14} />
            ) : undefined}
          </Top>

          <Bottom>
            <Title>
              <span>{pair.quote.title}</span>
            </Title>
            <CurrencyInformationClipboardStyled
              fullWidth
              currency={pair.quote}
            />
            <ProviderBadge pair={pair} />
            <PairInformationAge pair={pair} />
          </Bottom>
        </CurrencyInformation>
      </PairInformationRoot>
    )
  },
)

export const PairInformationRoot = styled.div`
  display: grid;
  justify-items: start;
  align-items: center;
  grid-template-columns: auto auto minmax(0, 1fr);
  padding-block: 10px;
  padding-inline-end: 10px;

  ${mediaMaxMobile(css`
    padding-block: 4px;
    padding-inline-end: 6px;
  `)}
`

const CurrencyInformationClipboardStyled = styled(CurrencyInformationClipboard)`
  color: var(--text-11);
`

const Favorite = styled(PairInformationFavorite)`
  margin-inline-end: 8px;

  ${mediaMaxMobile(css`
    display: none;
  `)}
`

const CurrencyInformation = styled.div`
  display: grid;

  grid-template-rows: auto auto;
  justify-items: start;
  align-items: center;

  margin-inline-start: 10px;
  gap: 1px;
`

const Top = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;

  align-items: center;

  gap: 6px;
`

const Bottom = styled.div`
  color: var(--background-11);
  display: grid;
  grid-auto-flow: column;
  gap: 4px;

  justify-items: start;
  align-items: center;
  justify-content: start;

  width: 100%;
  overflow: hidden;

  > svg {
    cursor: pointer;
  }
`

const Ticker = styled(PairTicker)`
  all: unset;
  cursor: pointer;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  font-size: 12px;
  font-weight: 700;
  line-height: 115%;
  letter-spacing: 0.48px;
`

const Title = styled(Typography)`
  color: var(--text-11);

  line-height: 16px;

  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`

const SearchLoupe = styled(CurrencyAddressSearchLoupe)`
  width: 14px;
  height: 15px;
  padding: 0;
`
